import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';

export const useGameEcosystem = () => {
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;
  const userType = supabaseUser ? 'supabase' : 'internal';

  // Handle game creation notifications to all stakeholders
  const notifyGameCreated = useCallback(async (gameId: string, gameData: any) => {
    if (!user) return;

    try {
      // Notify venue managers
      if (gameData.venue_id) {
        await supabase.from('game_notifications_enhanced').insert({
          game_id: gameId,
          recipient_id: gameData.venue_id,
          recipient_type: 'supabase',
          notification_type: 'game_created',
          title: 'New Game Created',
          message: `A new game "${gameData.game_name}" has been scheduled at your venue for ${gameData.game_date} at ${gameData.game_time}.`,
          action_required: true,
          action_type: 'approve_game',
          priority: 'high'
        });
      }

      // Notify team coaches and owners
      const teams = [gameData.home_team_id, gameData.away_team_id].filter(Boolean);
      
      for (const teamId of teams) {
        // Get team coaches
        const { data: coaches } = await supabase
          .from('team_coaches')
          .select('coach_id')
          .eq('team_id', teamId);

        // Get team owner
        const { data: team } = await supabase
          .from('teams')
          .select('created_by')
          .eq('team_id', teamId)
          .single();

        // Notify coaches
        if (coaches) {
          for (const coach of coaches) {
            await supabase.from('game_notifications_enhanced').insert({
              game_id: gameId,
              recipient_id: coach.coach_id,
              recipient_type: 'supabase',
              notification_type: 'game_assignment',
              title: 'Team Assigned to Game',
              message: `Your team has been assigned to "${gameData.game_name}" on ${gameData.game_date} at ${gameData.game_time}.`,
              action_required: true,
              action_type: 'confirm_participation',
              priority: 'high'
            });
          }
        }

        // Notify team owner
        if (team?.created_by) {
          await supabase.from('game_notifications_enhanced').insert({
            game_id: gameId,
            recipient_id: team.created_by,
            recipient_type: 'supabase',
            notification_type: 'game_assignment',
            title: 'Team Assigned to Game',
            message: `Your team has been assigned to "${gameData.game_name}" on ${gameData.game_date} at ${gameData.game_time}.`,
            action_required: true,
            action_type: 'confirm_participation',
            priority: 'high'
          });
        }
      }

      // Create scorekeeper assignment notification (admin notification)
      await supabase.from('admin_notifications').insert({
        notification_type: 'scorekeeper_assignment_needed',
        title: 'Scorekeeper Assignment Required',
        message: `Game "${gameData.game_name}" on ${gameData.game_date} needs a scorekeeper assignment.`,
        related_entity_id: gameId,
        related_entity_type: 'game',
        priority: 'normal',
        created_by: user.id
      });

    } catch (error) {
      console.error('Error sending game creation notifications:', error);
    }
  }, [user, userType]);

  // Subscribe to real-time game notifications
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('game-ecosystem-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'games'
        },
        (payload) => {
          console.log('🎮 New game created:', payload.new);
          // Auto-trigger notification system
          notifyGameCreated(payload.new.id, payload.new);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'game_notifications_enhanced',
          filter: `recipient_id=eq.${user.id}`
        },
        (payload) => {
          console.log('🔔 New game notification:', payload.new);
          toast.info(payload.new.title, {
            description: payload.new.message
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, notifyGameCreated]);

  return {
    notifyGameCreated
  };
};
